import { Outlet } from 'react-router-dom';
import { Layout, Drawer, App } from 'antd';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutViewProvider } from '@shared/contexts/LayoutViewContext';
import { useMenuStore } from '@shared/store/menuStore';
import { useResponsive } from '@shared/hooks/useResponsive';
import { useSidebarMenu } from '@shared/hooks/useSidebarMenu';
import { useSidebarNavigation } from '@shared/hooks/useSidebarNavigation';
import { useTheme } from '@theme/useTheme';
import { AppHeader } from './AppHeader';
import { AppFooter } from './AppFooter';
import { SidebarContent } from './SidebarContent';
import { IncomingCallNotification } from '@features/customers/components/IncomingCallNotification';
import { PhoneSelectorPrompt } from '@features/users/components/PhoneSelectorPrompt';
import './DashboardLayout.css';

const { Sider, Content } = Layout;

const WAREHOUSE_MENU_FALLBACK_SESSION_KEY = 'warehouseMenuFallbackShown';

export function DashboardLayout() {
  const { t } = useTranslation(['translation', 'devices', 'menu']);
  const { theme: themeContext, isDark } = useTheme();
  const { token } = themeContext;
  const { message: messageApi } = App.useApp();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Используем готовый хук для определения размеров экрана
  const { isMobile, isTablet, isSmallMobile, isLarge, width: viewportWidth, height } = useResponsive();
  const isShortSidebarHeight = height < 720;
  
  // Используем openKeys из menuStore для сохранения состояния между сессиями
  const { openKeys, setOpenKeys, editMode, toggleEditMode: toggleEditModeBase } = useMenuStore();
  const toggleEditMode = useCallback(() => toggleEditModeBase(), [toggleEditModeBase]);

  // Сохраняем openKeys перед сворачиванием для восстановления после разворачивания
  const [preCollapseOpenKeys, setPreCollapseOpenKeys] = useState<string[]>([]);

  // Загружаем данные меню через хук
  const {
    menuItems,
    taskMenuQuery,
    warehouseMenuQuery,
    cableMenuQuery,
    warehouseMenuStatus,
    isWarehouseMenuEmpty,
  } = useSidebarMenu();

  // Навигация через хук
  const { selectedKeys, openKeys: navigationOpenKeys, setOpenKeys: setNavigationOpenKeys, handleMenuClick } = useSidebarNavigation(menuItems, isMobile);

  // ✅ При загрузке проверяем что openKeys содержит максимум 1 элемент (accordion)
  useEffect(() => {
    if (openKeys && openKeys.length > 1) {
      // Исправляем множественное раскрытие из localStorage
      setOpenKeys([openKeys[0]]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Обработка сворачивания/разворачивания сайдбара
  useEffect(() => {
    // Только для десктопа (не мобильные устройства)
    if (!isMobile) {
      if (collapsed) {
        // При сворачивании: сохраняем текущие openKeys и очищаем их
        if (openKeys && openKeys.length > 0) {
          setPreCollapseOpenKeys(openKeys);
        }
        // НЕ очищаем openKeys здесь - DraggableMenu сам обработает через inlineCollapsed
      } else {
        // При разворачивании: восстанавливаем сохраненные openKeys
        if (preCollapseOpenKeys.length > 0) {
          setOpenKeys(preCollapseOpenKeys);
          setPreCollapseOpenKeys([]);
        }
      }
    }
  }, [collapsed, isMobile, openKeys, setOpenKeys]); // eslint-disable-line react-hooks/exhaustive-deps

  // Отключаем режим редактирования при сворачивании сайдбара
  useEffect(() => {
    if (!isMobile && collapsed && editMode) {
      toggleEditMode();
      messageApi.info(
        t('menu_settings.edit_mode_auto_disabled') || 'Режим редактирования отключен для компактного меню',
      );
    }
  }, [collapsed, editMode, isMobile, messageApi, t, toggleEditMode]);

  // Блокировка скролла body при открытом мобильном меню
  useEffect(() => {
    if (isMobile && mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobile, mobileMenuOpen]);

  // Обработка предупреждений для динамических меню
  const taskMenuWarningShownRef = useRef(false);
  const warehouseMenuWarningShownRef = useRef(false);
  const warehouseMenuEmptyShownRef = useRef(false);
  const cableMenuWarningShownRef = useRef(false);
  const isTaskMenuFallback = taskMenuQuery.data === null;
  const isWarehouseMenuFallback = warehouseMenuQuery.data === null;

  useEffect(() => {
    if (
      taskMenuWarningShownRef.current ||
      (!taskMenuQuery.isError && !isTaskMenuFallback)
    ) {
      return;
    }

    taskMenuWarningShownRef.current = true;
    messageApi.warning(t('menu:tasks_dynamic_menu_unavailable'));
  }, [isTaskMenuFallback, messageApi, t, taskMenuQuery.isError]);

  useEffect(() => {
    const sessionShown =
      typeof window !== 'undefined' &&
      window.sessionStorage.getItem(WAREHOUSE_MENU_FALLBACK_SESSION_KEY) === '1';

    if (
      warehouseMenuWarningShownRef.current ||
      sessionShown ||
      (!warehouseMenuQuery.isError && !isWarehouseMenuFallback)
    ) {
      return;
    }

    warehouseMenuWarningShownRef.current = true;
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(WAREHOUSE_MENU_FALLBACK_SESSION_KEY, '1');
    }
    messageApi.warning(t('menu:warehouse_dynamic_menu_unavailable'));
  }, [isWarehouseMenuFallback, messageApi, t, warehouseMenuQuery.isError]);

  useEffect(() => {
    if (warehouseMenuStatus === 'ready') {
      warehouseMenuWarningShownRef.current = false;
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem(WAREHOUSE_MENU_FALLBACK_SESSION_KEY);
      }
    }
  }, [warehouseMenuStatus]);

  useEffect(() => {
    if (cableMenuWarningShownRef.current || !cableMenuQuery.isError) {
      return;
    }

    cableMenuWarningShownRef.current = true;
    messageApi.warning(t('menu:cable_dynamic_menu_unavailable'));
  }, [cableMenuQuery.isError, messageApi, t]);

  useEffect(() => {
    if (!cableMenuQuery.isError) {
      cableMenuWarningShownRef.current = false;
    }
  }, [cableMenuQuery.isError]);

  useEffect(() => {
    if (!isWarehouseMenuEmpty || warehouseMenuEmptyShownRef.current) {
      return;
    }
    warehouseMenuEmptyShownRef.current = true;
    messageApi.info(t('menu:warehouse_no_sections'));
  }, [isWarehouseMenuEmpty, messageApi, t]);

  // Обработчик клика с закрытием мобильного меню
  const handleMenuClickWithClose = useCallback(
    (e: { key: string }) => {
      handleMenuClick(
        e as Parameters<NonNullable<typeof handleMenuClick>>[0],
      );
      if (isMobile) {
        setMobileMenuOpen(false);
      }
    },
    [handleMenuClick, isMobile],
  );

  // Вычисление размеров сайдбара
  const isCollapsedDesktop = !isMobile && collapsed;
  const expandedSidebarWidth = isLarge ? 280 : isTablet ? 220 : 250;
  const collapsedSidebarWidth = 80;
  const resolvedSidebarWidth = isMobile ? 0 : collapsed ? collapsedSidebarWidth : expandedSidebarWidth;
  const contentPaddingX = isMobile ? token.paddingSM : token.paddingLG;
  const contentPaddingY = isMobile ? token.paddingSM : token.paddingLG;

  // Контекст для LayoutViewProvider
  const layoutContextValue = useMemo(
    () => ({
      sidebarWidth: resolvedSidebarWidth,
      expandedSidebarWidth,
      collapsedSidebarWidth,
      isSidebarCollapsed: collapsed,
      isMobile,
      isTablet,
      isDesktop: !isMobile,
      viewportWidth,
      contentPaddingX,
      contentPaddingY,
    }),
    [
      collapsed,
      collapsedSidebarWidth,
      contentPaddingX,
      contentPaddingY,
      expandedSidebarWidth,
      isMobile,
      isTablet,
      resolvedSidebarWidth,
      viewportWidth,
    ],
  );

  // Обработчик открытия/закрытия ключей меню
  const handleOpenKeysChange = useCallback(
    (keys: string[]) => {
      if (isCollapsedDesktop) {
          return;
        }
      setNavigationOpenKeys(keys);
    },
    [isCollapsedDesktop, setNavigationOpenKeys],
  );

  return (
    <LayoutViewProvider value={layoutContextValue}>
      <Layout className="min-h-screen bg-white dark:bg-gray-900">
      {/* Мобильный Drawer */}
      {isMobile ? (
        <Drawer
          placement="left"
          onClose={() => setMobileMenuOpen(false)}
          open={mobileMenuOpen}
          closeIcon={null}
          size={isSmallMobile ? viewportWidth : 280}
          styles={{
            body: {
              padding: 0,
              backgroundColor: token.colorBgContainer,
              overflowX: 'hidden',
            },
            header: { display: 'none' },
          }}
          className={`sidebar-scroll print:hidden ${isDark ? 'sidebar-scroll-dark' : ''}`}
        >
            <SidebarContent
              menuItems={menuItems}
              selectedKeys={selectedKeys}
              openKeys={navigationOpenKeys}
              onOpenChange={handleOpenKeysChange}
              onClick={handleMenuClickWithClose}
              collapsed={collapsed}
              isMobile={isMobile}
              isCollapsedDesktop={isCollapsedDesktop}
              isShortSidebarHeight={isShortSidebarHeight}
              onMenuNavigate={() => setMobileMenuOpen(false)}
            />
        </Drawer>
      ) : (
        /* Десктопный Sider.
           print:hidden — навигация не печатается. Замер 2026-09-07 (цикл 52):
           на странице печати карточки кросса (`?print=print`) узлов с `print:hidden`
           было НОЛЬ, и на бумагу уходило всё меню («Главная | Абоненты | Тарифы …»)
           рядом с карточкой. Печать — отдельная поверхность (каталог операторской
           читаемости, строка 7), и она общая: таких экранов десять, а слой один. */
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={expandedSidebarWidth}
          collapsedWidth={80}
          className={`sidebar-scroll print:hidden ${isDark ? 'sidebar-scroll-dark' : ''}`}
          style={{
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            backgroundColor: token.colorBgContainer,
            overflowY: 'auto',
            overflowX: 'hidden',
            zIndex: 100,
          }}
        >
            <SidebarContent
              menuItems={menuItems}
              selectedKeys={selectedKeys}
              openKeys={navigationOpenKeys}
              onOpenChange={handleOpenKeysChange}
              onClick={handleMenuClickWithClose}
              collapsed={collapsed}
              isMobile={isMobile}
              isCollapsedDesktop={isCollapsedDesktop}
              isShortSidebarHeight={isShortSidebarHeight}
            />
        </Sider>
      )}

      <Layout 
        style={{
          marginLeft: resolvedSidebarWidth,
          transition: 'margin-left 0.2s',
        }}
      >
        <AppHeader 
          collapsed={collapsed} 
          onCollapse={() => setCollapsed(!collapsed)}
          isMobile={isMobile}
          onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
        />

        <Content 
          className={isMobile ? 'm-2 p-3' : 'm-4 p-4'}
          style={{
            minHeight: 'calc(100vh - 52px - 48px)', // ✅ КОМПАКТНОСТЬ: header 52px + footer 48px
            backgroundColor: token.colorBgLayout,
          }}
        >
          <Outlet />
        </Content>

        {/* Footer */}
        <AppFooter 
          showSupport={true} 
          showPerformance={import.meta.env.DEV} 
        />
      </Layout>
      
      {/* Уведомления о входящих звонках */}
      <IncomingCallNotification />

      {/* Нудж «выберите активный IP-телефон» — один раз на вход, если WN_ASCALL включён */}
      <PhoneSelectorPrompt />
    </Layout>
    </LayoutViewProvider>
  );
}
